// frontend/src/lib/domains/recipient.ts
import { request } from "../core/request";

export type RecipientAccount = {
  id?: number;
  currency?: string;
  iban?: string | null;
  account_number?: string | null;
  routing_number?: string | null;
  bank_bic?: string | null;
  bank_name?: string | null;
  bank_address?: string | null;
  bank_country?: string | null;
  intermediary_bank_name?: string | null;
  intermediary_bank_bic?: string | null;
};

export type Recipient = {
  id: number;
  name: string;
  type?: string;
  currency?: string;
  full_address?: string | null;
  accounts: RecipientAccount[];
  created_at?: string;
};

/**
 * Safely unwraps list responses that might be:
 * - [ ... ]
 * - { data: [ ... ] }
 * - { data: { data: [ ... ] } }
 */
function unwrapListResponse(res: any): any[] {
  if (!res) return [];

  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (res.data && Array.isArray(res.data.data)) return res.data.data;

  return [];
}

/**
 * Normalize a single raw recipient from XAPI into the SPA shape.
 * Handles both:
 * - old:  { account: { ... } }
 * - new:  { accounts: [ ... ] } or { accounts: { ... } }
 */
function normalizeRecipient(raw: any): Recipient {
  if (!raw) {
    return {
      id: 0,
      name: "",
      type: undefined,
      currency: undefined,
      full_address: undefined,
      accounts: [],
      created_at: undefined,
    };
  }

  // Determine the accounts source (array / single object / legacy account)
  let accountsSource: any[] = [];

  if (Array.isArray(raw.accounts)) {
    accountsSource = raw.accounts;
  } else if (raw.accounts && typeof raw.accounts === "object") {
    accountsSource = [raw.accounts];
  } else if (raw.account) {
    // legacy shape from older XAPI
    accountsSource = [raw.account];
  }

  const accounts: RecipientAccount[] = accountsSource.map((a: any, index: number) => ({
    id: a?.id ?? a?.recipient_account_id ?? index,
    currency: a?.currency ?? raw.currency ?? undefined,
    iban: a?.iban ?? null,
    account_number: a?.account_number ?? null,
    routing_number: a?.routing_number ?? null,
    bank_bic: a?.bank_bic ?? null,
    bank_name: a?.bank_name ?? null,
    bank_address: a?.bank_address ?? null,
    bank_country: a?.bank_country ?? null,
    intermediary_bank_name: a?.intermediary_bank_name ?? null,
    intermediary_bank_bic: a?.intermediary_bank_bic ?? null,
  }));

  const primaryCurrency =
    raw.currency ??
    (accounts.length > 0 ? accounts[0].currency : undefined);

  return {
    id: raw.id,
    name: raw.name,
    type: raw.type,
    currency: primaryCurrency,
    full_address: raw.full_address ?? null,
    accounts,
    created_at: raw.created_at,
  };
}

/**
 * GET /xapi/recipients
 * Returns all recipients in normalized shape.
 */
export async function apiRecipientsIndex(): Promise<Recipient[]> {
  const res = await request<any>("/xapi/recipients", { method: "GET" });
  const items = unwrapListResponse(res);
  return items.map(normalizeRecipient);
}

/**
 * Alias used by transfer flows – kept for compatibility.
 * Same result as apiRecipientsIndex().
 */
export async function apiRecipientsWithAccounts(): Promise<Recipient[]> {
  return apiRecipientsIndex();
}

/**
 * GET /xapi/recipients/{id}
 */
export async function apiRecipientShow(
  id: number | string
): Promise<Recipient> {
  const res = await request<any>(`/xapi/recipients/${id}`, { method: "GET" });

  // Could be { data: {...} } or just {...}
  const raw = (res && (res.data ?? res)) as any;
  return normalizeRecipient(raw);
}

/**
 * GET /xapi/recipients/options
 * (multi-currency, types, rails)
 */
export async function apiRecipientOptions(): Promise<any> {
  const res = await request<any>("/xapi/recipients/options", { method: "GET" });
  return res.data ?? res;
}

/**
 * POST /xapi/recipients
 */
export async function apiRecipientCreate(payload: any): Promise<Recipient> {
  const res = await request<any>("/xapi/recipients", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const raw = (res && (res.data ?? res)) as any;
  return normalizeRecipient(raw);
}

/**
 * PUT /xapi/recipients/{id}
 */
export async function apiRecipientUpdate(
  id: number | string,
  payload: any
): Promise<Recipient> {
  const res = await request<any>(`/xapi/recipients/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  const raw = (res && (res.data ?? res)) as any;
  return normalizeRecipient(raw);
}

/**
 * DELETE /xapi/recipients/{id}
 */
export async function apiRecipientDelete(
  id: number | string
): Promise<void> {
  await request(`/xapi/recipients/${id}`, { method: "DELETE" });
}
